import { useEffect, useState } from "react";
import Heading from "../../component/Heading/Heading";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import EuroIcon from "@mui/icons-material/Euro";
import CommonButton from "../../component/button/Button";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { Grid, FormControlLabel, Switch } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from "react-i18next";

// Services
import { getBillingPlans, paymentConfirmed } from "../../services/billing-services/billingServices";
import { getBillingStatus } from "../../services/customer-services/customerBillingStatus";
import { createCheckoutSession, createPortalSession } from "../../services/customer-services/paymentService";

// Components & Assets
import CommonModal from "../../component/model/ModelComponent";
import Toast from "../../component/toastify/Index";
import checkIcon from "../../assets/icons/check.svg";
import { formatAmount } from "../../helper/formatAmount";

import "../../styles/pages/billing/billing.scss";

const BillingCard = styled(Card)({
  backgroundColor: '#fff !important',
  border: '2px solid #000 !important',
  borderRadius: '20px !important',
});

const ToggleSwitch = styled(Switch)(({ theme }) => ({
  width: 67,
  height: 30,
  padding: 0,
  '& .MuiSwitch-track': {
    borderRadius: 40,
    backgroundColor: '#ffffff',
    border: '1px solid #000000',
    opacity: 1,
    width: '100%',
    height: '100%',
    transition: theme.transitions.create(['background-color'], { duration: 500 }),
  },
  '& .MuiSwitch-switchBase': {
    padding: '4px 0 4px 9px',
    '&.Mui-checked': {
      transform: 'translateX(30px)',
      '& + .MuiSwitch-track': {
        backgroundColor: '#ffffff',
        border: '1px solid #000000',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: 'none',
    width: 22,
    height: 22,
    background: 'linear-gradient(45deg, #000000, #40330D)',
    transition: theme.transitions.create(['transform'], { duration: 500 }),
  },
}));

const Billing = () => {
  const { t } = useTranslation();
  
  // States
  const [plans, setPlans] = useState([]);
  const [billingStatus, setBillingStatus] = useState(null);
  const [subscribedPlan, setSubscribedPlan] = useState('');
  const [isYearly, setIsYearly] = useState(false);
  const [isFreeTrialPurchased, setIsFreeTrialPurchased] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Modal State for Free Trial
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false);
  const [trialPlanId, setTrialPlanId] = useState("");

  const handleSwitchChange = (event) => setIsYearly(event.target.checked);

  const fetchData = async () => {
    try {
      const planRes = await getBillingPlans();
      const statusRes = await getBillingStatus();
      
      const allPlans = planRes?.billingPlanList || [];
      const freeTrialPlan = allPlans.find(p => p.planName === "Free Trial");
      const paidPlans = allPlans.filter(p => p.planName !== "Free Trial");
      
      setPlans([freeTrialPlan, ...paidPlans].filter(Boolean));
      setBillingStatus(statusRes);
      setSubscribedPlan(statusRes?.planName || "");
      setIsFreeTrialPurchased(statusRes?.isFreeTrialPurchased || false);
    } catch (err) {
      console.error("Error fetching billing data", err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // NEW Stripe Checkout Handler (Handles both Buy and Upgrade)
  const handleStripeAction = async (planId) => {
    try {
      setLoading(true);
      const frequency = isYearly ? "yearly" : "monthly";
      const response = await createCheckoutSession(planId, frequency);
      if (response?.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      Toast(t("error_initiating_payment"), "error");
    } finally {
      setLoading(false);
    }
  };

  // NEW Stripe Portal Handler
  const handleManageSubscription = async () => {
    try {
      setLoading(true);
      const response = await createPortalSession();
      if (response?.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      Toast(t("error_initiating_portal"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmFreeTrial = async () => {
    try {
      const response = await paymentConfirmed(trialPlanId, true, false);
      if (response?.isSuccess) {
        Toast(response?.message, "success");
        setIsTrialModalOpen(false);
        fetchData();
      }
    } catch (error) {
      Toast("Error activating trial", "error");
    }
  };

  const freeTrialPlan = plans.find(p => p.planName === "Free Trial");
  const paidPlans = plans.filter(p => p.planName !== "Free Trial");

  return (
    <div className="billing">
      <CommonModal
        open={isTrialModalOpen}
        onClose={() => setIsTrialModalOpen(false)}
        title={t('billing_free_subscription')}
      >
        <Typography variant="body1" sx={{ mb: 3 }}>
          {t('billing_free_trial_description')}
        </Typography>
        <Typography className="model-action-btn flex">
            <CommonButton text={t('payment_status_cancel')} className="cancle-btn" bgColor="#E14A4A" onClick={() => setIsTrialModalOpen(false)} />
            <CommonButton text={t('billing_confirmed')} className="add-user-btn" bgColor="linear-gradient(to right, #F5D01C, #826320)" onClick={handleConfirmFreeTrial} />
        </Typography>
      </CommonModal>

      <div className="billing-main-heading flex items-center justify-between">
        <Heading text={t("module_billing")} className="billing-heading" />
        {billingStatus?.stripeSubscriptionId && (
          <CommonButton 
            text={t('manage_subscription')} 
            onClick={handleManageSubscription}
            disabled={loading}
            className="manage-btn"
          />
        )}
      </div>

      <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: "column", alignItems: 'center', mb: 10 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography className={!isYearly ? 'dark-blue' : ''} sx={{ pr: 3.5, fontSize: 18, fontWeight: 500 }}>{t('billing_plan_monthly')}</Typography>
          <FormControlLabel control={<ToggleSwitch checked={isYearly} onChange={handleSwitchChange} />} label="" />
          <Typography className={isYearly ? 'dark-blue' : ''} sx={{ pr: 3.5, fontSize: 18, fontWeight: 500 }}>{t('billing_plan_yearly')}</Typography>
        </Box>
      </Box>

      <Grid container rowSpacing={{ xs: 6 }} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        {/* Free Trial Plan */}
        {/* {freeTrialPlan && (
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <BillingCard variant="outlined" sx={{ display: 'flex', flexDirection: 'column' }}>
              <CardContent className="billing-details" sx={{ flexGrow: 1 }}>
                <Heading text={freeTrialPlan.planName} className="billing-heading" />
                <Typography className="billing-price flex items-center">{t('billing_free_days')}</Typography>
                <Typography className="discount-price flex items-center">{formatAmount(0)} <EuroIcon className="discount-icon" /></Typography>
                <CommonButton
                  text={subscribedPlan === "Free Trial" ? t('billing_plan_subscribed_plan') : t("billing_choose_plan")}
                  disabled={isFreeTrialPurchased || subscribedPlan !== "No Active Plan"  || loading}
                  className="add-user-btn w-100"
                  onClick={() => {
                    setTrialPlanId(freeTrialPlan._id);
                    setIsTrialModalOpen(true);
                  }}
                />
                <hr className="line" />
                <div className="list-items">
                  {freeTrialPlan.features.map((feature, idx) => (
                    <ListItem className="plan-fetures" key={idx}>
                      <Typography className="plan-icon"><img src={checkIcon} alt="" /></Typography>
                      <ListItemText className="plan-text" primary={feature} />
                    </ListItem>
                  ))}
                </div>
              </CardContent>
            </BillingCard>
          </Grid>
        )} */}

        {/* Paid Stripe Plans */}
        {paidPlans.map((plan) => {
          const selectedOption = isYearly ? plan.billingOptions?.yearly : plan.billingOptions?.monthly;
          const price = selectedOption?.price || 0;
          const discountPrice = selectedOption?.discountPrice ?? price;
          const isCurrentPlan = subscribedPlan === plan.planName;
          const isDowngrade = subscribedPlan === "Professional" && plan.planName === "Starter";

          const planName = plan.planName === "Starter" ? "Solo Selbständige" : plan.planName;

          return (
            <Grid item xs={12} md={6} lg={4} key={plan._id}>
              <Box className={plan.planName === "Professional" ? "customer_favourite" : ""} data-label={t("billing_plan_customer_favourite")}>
                <BillingCard variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent className="billing-details" sx={{ flexGrow: 1 }}>
                    <Heading text={planName} className="billing-heading" />
                    {
                    discountPrice > price && (<Typography className="billing-price flex items-center">
                      <span className="flex items-center spla-price">{formatAmount(discountPrice)} <EuroIcon className="plan-euro-icon" /></span>
                    </Typography>
                  )}
                    <Typography className="discount-price flex items-center">{formatAmount(price)} <EuroIcon className="discount-icon" /></Typography>
                    <CommonButton
                      text={loading ? "..." : isCurrentPlan ? t('billing_plan_subscribed_plan') : (subscribedPlan ? t('billing_plan_upgrade_plan') : t("billing_choose_plan"))}
                      disabled={loading || isCurrentPlan || isDowngrade}
                      className="add-user-btn w-100"
                      onClick={() => handleStripeAction(plan._id)}
                    />
                    <hr className="line" />
                    <div className="list-items">
                      {plan.features.map((feature, idx) => (
                        <ListItem className="plan-fetures" key={idx}>
                          <Typography className="plan-icon"><img src={checkIcon} alt="" /></Typography>
                          <ListItemText className="plan-text" primary={feature} />
                        </ListItem>
                      ))}
                    </div>
                  </CardContent>
                </BillingCard>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
};

export default Billing;